import React, { useState, useRef, useEffect } from "react";
import { ReactComponent as DragHandleIcon } from "Assets/svg/conditionBlock/dragHandle.svg";
import { ReactComponent as PinIcon } from "Assets/svg/pin.svg";
import { CATEGORY_LABEL, CATEGORY_COLOR_CLASS } from "../utils/widgetConstants";
import WidgetCardSkeleton from "./WidgetCardSkeleton";
import AudienceScopePopover from "./AudienceScopePopover";
import VisualizationTypePopover from "./VisualizationTypePopover";

const DotMenuButton = ({ onToggle, btnRef }) => (
  <button
    ref={btnRef}
    type="button"
    className="widgetCardDotMenuBtn"
    onClick={onToggle}
    title="Widget options"
  >
    <svg width="3" height="13" viewBox="0 0 3 13" fill="none">
      <circle cx="1.5" cy="1.5" r="1.5" fill="currentColor" />
      <circle cx="1.5" cy="6.5" r="1.5" fill="currentColor" />
      <circle cx="1.5" cy="11.5" r="1.5" fill="currentColor" />
    </svg>
  </button>
);

const DotMenuDropdown = ({
  wrapRef,
  onPin,
  onRemove,
  isPinned,
  isAdmin = false,
  onToggleMemberBreakdown,
  memberBreakdownEnabled = false,
  onToggleLiAccountBreakdown,
  liAccountBreakdownEnabled = false,
  showLiAccountBreakdown = false,
  onOpenAudienceScope,
  hasAudienceScope,
  scopeLabel,
  onOpenVisualization,
  hasVisualization,
  vizLabel,
  onClose
}) => (
  <div ref={wrapRef} className="widgetCardDotMenuDropdown">
    {isAdmin && hasAudienceScope && (
      <button
        type="button"
        className="widgetCardDotMenuItem"
        onClick={() => { onClose(); onOpenAudienceScope(); }}
      >
        <span className="widgetCardDotMenuLabel">Scope</span>
        <span className="widgetCardDotMenuValue">{scopeLabel}</span>
      </button>
    )}
    {hasVisualization && (
      <button
        type="button"
        className="widgetCardDotMenuItem"
        onClick={() => { onClose(); onOpenVisualization(); }}
      >
        <span className="widgetCardDotMenuLabel">{vizLabel}</span>
      </button>
    )}
    {isAdmin && onToggleMemberBreakdown && (
      <button
        type="button"
        className="widgetCardDotMenuItem"
        onClick={() => { onClose(); onToggleMemberBreakdown(); }}
      >
        <span className="widgetCardDotMenuLabel">Breakdown by member</span>
        <span className={`widgetCardDotMenuValue${memberBreakdownEnabled ? " widgetCardDotMenuValueOn" : ""}`}>
          {memberBreakdownEnabled ? "On" : "Off"}
        </span>
      </button>
    )}
    {/* Not admin-gated - any user can see their own LinkedIn accounts' breakdown. Only shown
        when the team actually has multi-LinkedIn-account usage (showLiAccountBreakdown). */}
    {showLiAccountBreakdown && onToggleLiAccountBreakdown && (
      <button
        type="button"
        className="widgetCardDotMenuItem"
        onClick={() => { onClose(); onToggleLiAccountBreakdown(); }}
      >
        <span className="widgetCardDotMenuLabel">Breakdown by LinkedIn account</span>
        <span className={`widgetCardDotMenuValue${liAccountBreakdownEnabled ? " widgetCardDotMenuValueOn" : ""}`}>
          {liAccountBreakdownEnabled ? "On" : "Off"}
        </span>
      </button>
    )}
    <button
      type="button"
      className="widgetCardDotMenuItem"
      onClick={() => { onClose(); onPin(); }}
    >
      <span className="widgetCardDotMenuLabel">{isPinned ? "Unpin" : "Pin to top"}</span>
    </button>
    <button
      type="button"
      className="widgetCardDotMenuItem widgetCardDotMenuItemDanger"
      onClick={() => { onClose(); onRemove(); }}
    >
      <span className="widgetCardDotMenuLabel">Remove widget</span>
    </button>
  </div>
);

const VIZ_LABELS = { LINE: "Line", BAR: "Bar", KPI: "KPI" };

const getScopeLabel = audienceScope => {
  if (!audienceScope?.type) return "Dashboard filter";
  if (audienceScope.type === "EVERYONE") return "Everyone";
  if (audienceScope.type === "SPECIFIC_USERS") {
    const n = audienceScope.memberIds?.length || 0;
    return `${n} member${n === 1 ? "" : "s"}`;
  }
  const n = audienceScope.teamIds?.length || 0;
  return `${n} team${n === 1 ? "" : "s"}`;
};

const WidgetCard = ({
  title,
  subtitle,
  category,
  isLoading = false,
  isPinned = false,
  onRemove,
  onPin,
  onViewRecords,
  isAdmin = false,
  audienceScope,
  onAudienceScopeChange,
  memberBreakdownEnabled = false,
  onToggleMemberBreakdown,
  liAccountBreakdownEnabled = false,
  onToggleLiAccountBreakdown,
  showLiAccountBreakdown = false,
  visualization,
  defaultVisualization = "LINE",
  onVisualizationChange,
  children
}) => {
  const colorClass = CATEGORY_COLOR_CLASS[category];
  const [showAudienceScope, setShowAudienceScope] = useState(false);
  const [showVisualization, setShowVisualization] = useState(false);
  const [showDotMenu, setShowDotMenu] = useState(false);
  const dotMenuBtnRef = useRef(null);
  const dotMenuDropdownRef = useRef(null);

  useEffect(() => {
    const handler = e => {
      if (
        dotMenuBtnRef.current && !dotMenuBtnRef.current.contains(e.target) &&
        dotMenuDropdownRef.current && !dotMenuDropdownRef.current.contains(e.target)
      ) {
        setShowDotMenu(false);
      }
    };
    if (showDotMenu) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showDotMenu]);

  return (
    <div className={`widgetCard${isPinned ? " widgetCardPinned" : ""}`}>
      <div className="widgetCardInner">
        {isPinned && <div className="widgetCardPinBar" />}
        <div className="widgetCardHeader">
          <div className="widgetCardTitleWrap">
            <div className="widgetCardTitle">
              {isPinned && <PinIcon className="widgetCardPinIcon" />}
              <span className="widgetCardTitleText">{title}</span>
              {category && (
                <span className={`widgetCardCategory${colorClass ? ` widgetCardCategory--${colorClass}` : ""}`}>
                  {CATEGORY_LABEL[category] || category}
                </span>
              )}
            </div>
            {subtitle && <div className="widgetCardSubtitle">{subtitle}</div>}
          </div>
          <div className="widgetCardActions">
            {onViewRecords && (
              <button
                type="button"
                className="widgetCardViewRecordsBtn"
                onClick={onViewRecords}
                title="View Records"
              >
                <i className="material-icons">bar_chart</i>
              </button>
            )}
            <span className="widgetCardDragHandle" title="Drag to reorder">
              <DragHandleIcon />
            </span>
            <div className="widgetCardDotMenuWrap">
              <DotMenuButton
                btnRef={dotMenuBtnRef}
                onToggle={() => setShowDotMenu(o => !o)}
              />
            </div>
          </div>
        </div>
        {isLoading ? <WidgetCardSkeleton /> : children}
      </div>
      {showDotMenu && (
        <DotMenuDropdown
          wrapRef={dotMenuDropdownRef}
          isPinned={isPinned}
          onPin={onPin}
          onRemove={onRemove}
          isAdmin={isAdmin}
          hasAudienceScope={!!onAudienceScopeChange}
          onOpenAudienceScope={() => setShowAudienceScope(true)}
          scopeLabel={getScopeLabel(audienceScope)}
          memberBreakdownEnabled={memberBreakdownEnabled}
          onToggleMemberBreakdown={onToggleMemberBreakdown}
          liAccountBreakdownEnabled={liAccountBreakdownEnabled}
          onToggleLiAccountBreakdown={onToggleLiAccountBreakdown}
          showLiAccountBreakdown={showLiAccountBreakdown}
          hasVisualization={!!onVisualizationChange}
          onOpenVisualization={() => setShowVisualization(true)}
          vizLabel={VIZ_LABELS[visualization || defaultVisualization] || "Line"}
          onClose={() => setShowDotMenu(false)}
        />
      )}
      {showAudienceScope && (
        <AudienceScopePopover
          audienceScope={audienceScope}
          onClose={() => setShowAudienceScope(false)}
          onSave={onAudienceScopeChange}
        />
      )}
      {showVisualization && (
        <VisualizationTypePopover
          visualization={visualization}
          defaultVisualization={defaultVisualization}
          onClose={() => setShowVisualization(false)}
          onSave={onVisualizationChange}
        />
      )}
    </div>
  );
};

export default WidgetCard;
